import Modal from "components/atoms/Modal";
import media from "helpers/media";
import styled, { createGlobalStyle } from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2.4rem;
  font-family: "Roboto", sans-serif;
  width: 100%;
`;

export const DashboardRow = styled.div`
  display: flex;
  gap: 2rem;
  width: 116rem;

  ${media.MD`
    flex-direction: column;
    width: 100%;
    

  `}
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding: 1.6rem 3.2rem;

  ${media.MD`
    margin: auto;
    width: 100%;
  `}
`;

export const AptitudeCard = styled(Card)`
  @media print {
    height: auto;
    margin: auto;
  }
`;

export const InterestCard = styled(Card)`
  flex: 1;
  gap: 2.4rem;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.6rem;
`;

export const CardTitle = styled.h3`
  font-family: "Roboto", sans-serif;
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 1.14;
  letter-spacing: 8.57%;
  color: #59626c !important;
  text-align: center;
  color: ${({ theme }) => theme.colors.manatee};
  margin: 0;
`;

export const DetailsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1.6rem 0.6rem 1.2rem;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.geyser};
  border-radius: 0.4rem;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 1.4rem;
  line-height: 1.71;
  letter-spacing: 1.43%;
  color: ${({ theme }) => theme.colors.mineShaft};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.whiteLilac};
  }
`;

export const DetailsText = styled.span`
  ${media.SM`
    display: none;
  `}
`;

export const InfoIcon = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 1.6rem;
    height: 1.6rem;
    background: ${({ theme }) => theme.colors.denim};
    border-radius: 50%;
  }

  &::after {
    content: "i";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.colors.white};
    font-size: 1rem;
    font-weight: bold;
  }
`;

// Aptitude Skills Components
export const AptitudeContent = styled.div`
  width: 40.4rem;
  height: 34.8rem;
  position: relative;
`;

export const PieChartContainer = styled.div`
  position: absolute;
  top: -1rem;
  left: 3.7rem;
  width: 31rem;
  height: 31rem;
  display: flex;
  justify-content: center;
  align-items: center;

  @media print {
    bottom: 20px;
    left: 150px;
    width: 100%;
    height: 100%;
    position: relative;
  }
`;

export const LegendContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3.2rem;
`;

export const LegendItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.2rem;
  width: 11.1rem;
  height: 3.4rem;
`;

export const LegendItemContent = styled.div`
  display: flex;
  gap: 0.6rem;
`;

export const LegendColorBox = styled.div<{ color: string }>`
  width: 0.4rem;
  height: 3.2rem;
  background: ${({ color }) => color};
  border-radius: 0.2rem;
`;

export const LegendTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

export const LegendLabel = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  line-height: 1;
  letter-spacing: 2.5%;
  color: ${({ theme }) => theme.colors.shuttleGray};
`;

export const LegendValue = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.14;
  letter-spacing: 1.43%;
  color: ${({ theme }) => theme.colors.manatee};
`;

export const InfoIconSmall = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  opacity: 0.6;
  cursor: help;

  &::before {
    content: "";
    position: absolute;
    width: 1.2rem;
    height: 1.2rem;
    border: 1.5px solid ${({ theme }) => theme.colors.shuttleGray};
    border-radius: 50%;
    top: 0.2rem;
    left: 0.2rem;
  }

  &::after {
    content: "i";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.colors.shuttleGray};
    font-size: 0.8rem;
    font-weight: bold;
  }
`;

export const ChartPercentage = styled.div<{
  top: string;
  left: string;
  align?: string;
}>`
  position: absolute;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  font-family: "Roboto Slab", serif;
  font-weight: 500;
  font-size: 1.8rem;
  line-height: 1.32;
  color: ${({ theme }) => theme.colors.white};
  text-align: ${({ align = "left" }) => align};
`;

// Interest Inventory Components
export const InterestContent = styled.div<{ showOverflow?: boolean }>`
  width: 44.9rem;
  position: relative;

  ${media.MD`
    width: 100%;
    height: ${(props: { showOverflow?: boolean }) =>
      props.showOverflow ? "100%" : "140px"};
    overflow: ${(props: { showOverflow?: boolean }) =>
      props.showOverflow ? "visible" : "hidden"};
  `}
`;

export const InterestRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  height: 2.4rem;
  margin-bottom: 0.4rem;
  flex-direction: row;

  ${media.MD`
   flex-direction: column;
   align-items: flex-start;
   gap: 0;
   margin: 0;
   height: auto;
  `}
`;

export const InterestLabel = styled.div`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 1.4rem;
  line-height: 1.6rem;
  letter-spacing: 4.44%;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mineShaft};
  text-align: right;
  width: 22rem;
  flex-shrink: 0;

  ${media.MD`
    width: 100%;
    text-align: left;
    padding: 0;
  `}
`;

export const InterestBar = styled.div<{ width: string; opacity?: number }>`
  height: 2rem;
  position: relative;
  flex-shrink: 0;

  &::before {
    content: "";
    width: ${({ width }) => width};
    height: 1.6rem;
    background: ${({ theme }) => theme.colors.denim};
    opacity: ${({ opacity = 1 }) => opacity};
    border-radius: 0 0.2rem 0.2rem 0;
    display: block;
  }
`;

export const InterestValue = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 1.2rem;
  line-height: 1.17;
  color: ${({ theme }) => theme.colors.mineShaft};
  margin-left: 0.8rem;
  flex-shrink: 0;
`;

export const InterestChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 0.3rem;
  ${media.MD`
    gap: 1rem;
  `}
`;

// Career Suggestions List Components
export const CareersCard = styled.div<{ showOverflow?: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.8rem;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 3.3rem;
  padding: 3.2rem;
  width: 116.1rem;
  margin-top: 2.4rem;

  ${media.MD`
    width: 100%;
  
  `}
`;

export const CareersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 28rem;
`;

export const CareersTitle = styled.h2`
  font-family: "Roboto Slab", serif;
  font-weight: 600;
  font-size: 2.4rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.mineShaft};
  margin: 0;
  display: flex;
  align-items: center;
  height: 3.2rem;
`;

export const CareersList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CareerItem = styled.div<{ isAlternate?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4rem;
  padding: 1.8rem 2.4rem;
  width: 109.8rem;
  min-height: 5.2rem;
  background: ${({ isAlternate, theme }) =>
    isAlternate ? "#F3F5FB" : theme.colors.white};

  ${media.MD`
    width: 100%;
    padding-bottom: 0;
  `}
  @media print {
    display: none;
  }

  &.favorited {
    @media print {
      display: flex;
    }
  }
`;

export const CareerName = styled.div`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.14;
  letter-spacing: 2.86%;
  color: ${({ theme }) => theme.colors.mineShaft};
  flex: 1;
`;

export const StarIcon = styled.div<{ isFavorited?: boolean }>`
  width: 1.6rem;
  height: 1.6rem;
  position: relative;

  &::before {
    content: "★";
    position: absolute;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    color: ${({ isFavorited, theme }) =>
      isFavorited ? theme.colors.treePoppy : theme.colors.shuttleGray};
    line-height: 1;
  }
`;

export const LearnMoreSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;

  @media print {
    display: none;
  }
`;

export const CarrerDescription = styled.div`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.14;
  color: ${({ theme }) => theme.colors.mineShaft};
  flex: 1;

  display: none;

  @media print {
    display: block;
  }
`;

export const LearnMoreText = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.denim};
`;

export const ArrowIcon = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  position: relative;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.denim};
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "→";
    color: ${({ theme }) => theme.colors.white};
    font-size: 1rem;
    font-weight: bold;
    line-height: 1;
  }
`;

// Skills Breakdown Styles
export const SkillsBreakdownContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const SkillsBreakdownTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
`;

export const SkillCategory = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SkillCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.fuelYellow};
`;

export const SkillCategoryTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  text-transform: uppercase;
`;

export const SkillCategoryPercentage = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
`;

export const SkillsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const SkillItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SkillName = styled.span`
  font-size: 0.875rem;
  color: #666;
  flex: 1;
`;

export const SkillBarContainer = styled.div`
  flex: 2;
  height: 0.5rem;
  background: #f0f0f0;
  border-radius: 0.25rem;
  margin: 0 1rem;
  overflow: hidden;
`;

export const SkillBar = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${(props) => props.percentage}%;
  background: ${(props) => props.color};
  border-radius: 0.25rem;
  transition: width 0.3s ease;
`;

export const SkillPercentage = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 2.5rem;
  text-align: right;
`;

// Study Resources Section Styles
export const StudyResourcesCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.8rem;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  padding: 3.2rem;
  margin-top: 2.4rem;
  width: 116.1rem;
  display: flex;
  flex-direction: column;
  gap: 3.3rem;

  ${media.MD`
    width: 100%;
  `}
`;

export const StudyResourcesHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

export const StudyResourcesTitle = styled.h2`
  font-family: "Roboto Slab", serif;
  font-weight: 600;
  font-size: 2.4rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.mineShaft};
  margin: 0;
`;

export const StudyResourcesContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.6rem;

  ${media.MD`
    flex-direction: column;
  `}
`;

export const StudySection = styled.div`
  width: 24.5rem;
  height: 12.2rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
`;

export const TouchIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SectionTitle = styled.h3`
  font-family: "Roboto", sans-serif;
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.treePoppy};
  margin: 0;
`;

export const ResourcesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-left: 3.2rem;
`;

export const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

export const BulletPoint = styled.div`
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.denim};
`;

export const ResourceLink = styled.a`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 1.17;
  color: ${({ theme }) => theme.colors.mineShaft};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.denim};
    text-decoration: underline;
  }
`;

export const SectionDivider = styled.div`
  width: 0.1rem;
  height: 12.2rem;
  background: ${({ theme }) => theme.colors.geyser};

  ${media.MD`
    display: none;
  `}
`;

export const InterestModal = styled(Modal)`
  width: 100%;
  max-width: 120rem;
  height: 100%;
  max-height: 80rem;
`;

export const ViewerAccessModal = styled(Modal)`
  width: 100%;
  max-width: 120rem;
  height: 100%;
  max-height: 80rem;
`;

export const InterestItem = styled.div`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

export const InterestModalContent = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

// Global style for ReactTooltip
export const TooltipGlobalStyle = createGlobalStyle`
  .__react_component_tooltip.custom-tooltip {
    max-width: 500px !important;
    word-wrap: break-word;
    padding: 2rem;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  }
`;

// Heading Styles
export const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  font-size: 2rem;
  padding: 1rem;
  font-weight: 700;
  line-height: 1.17;
  color: ${({ theme }) => theme.colors.manatee};
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
`;

export const HeadingText = styled.span`
  font-family: "Roboto", sans-serif;

  color: ${({ theme }) => theme.colors.mineShaft};
`;
